import {
  recordImpressionByParam,
  sendEvent,
} from "@JohnLewisPartnership/experiments-utils"; // eslint-disable-line import-x/namespace
import {
  parseExperiencesHeader,
  type FeatureFlag,
} from "@packages/feature-flags";
import { clientLogger } from "./clientLogger";

type MonetateParameters = NonNullable<
  Parameters<typeof recordImpressionByParam>[1][number]["parameters"]
>;

export function sendMonetateEvent(eventName: string) {
  clientLogger.log("Sending Monetate event:", eventName);
  sendEvent(eventName);
}

function getMonetateChannel(): string | undefined {
  const configContent = document.getElementById(
    "__EXPERIMENTATION_CONFIG__",
  )?.textContent;
  if (!configContent) return undefined;

  try {
    const config: unknown = JSON.parse(configContent);
    if (
      typeof config === "object" &&
      config !== null &&
      "monetateChannel" in config &&
      typeof config.monetateChannel === "string"
    ) {
      return config.monetateChannel;
    }
  } catch (error: unknown) {
    clientLogger.error("Failed to parse experimentation config:", error);
  }

  return undefined;
}

export function recordManagedImpression(
  flag: FeatureFlag | undefined,
  experiencesRawString: string | undefined,
  parameterKey: string,
): void {
  if (
    flag?.source !== "monetate" ||
    !flag.impressionId ||
    !flag.experienceName ||
    flag.experienceId === undefined ||
    !experiencesRawString
  ) {
    return;
  }

  const channel = getMonetateChannel();
  const monetateId = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith("mt.v="))
    ?.slice("mt.v=".length);

  if (!channel || !monetateId) {
    return;
  }

  let decodedMonetateId: string;
  try {
    decodedMonetateId = decodeURIComponent(monetateId);
  } catch (error: unknown) {
    clientLogger.error("Failed to decode Monetate visitor cookie:", error);
    return;
  }

  const experience = parseExperiencesHeader(experiencesRawString).find(
    (candidate) =>
      String(candidate.experienceId) === String(flag.experienceId) &&
      candidate.experienceName === flag.experienceName &&
      candidate.impressionId === flag.impressionId &&
      typeof candidate.parameters?.[parameterKey] === "string",
  );
  const parameterValue = experience?.parameters?.[parameterKey];

  if (!experience || typeof parameterValue !== "string") {
    return;
  }

  const experiencesForImpression: Parameters<
    typeof recordImpressionByParam
  >[1] = [
    {
      ...experience,
      impressionId: flag.impressionId,
      variantLabel: experience.variantLabel ?? "",
      parameters: (experience.parameters as MonetateParameters | null) ?? null,
    },
  ];

  void recordImpressionByParam(
    channel,
    experiencesForImpression,
    parameterKey,
    parameterValue,
    decodedMonetateId,
  ).catch((error: unknown) => {
    clientLogger.error("Failed to record managed impression:", error);
  });
}
